import { memo, useMemo } from 'react';
import styled from '@emotion/styled';

import { cleanUserInput } from '../../lib/cleanUserInput';
import { extractParamsFromMediaUrl } from '../../lib/mediaApiUrl';
import { themeFont } from '../../lib/themeFont';

import type { MediaUrlParams } from '../../lib/mediaApiUrl';
import type { ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import type { Wws_PersonItem } from '../../types/graphql';
import FixedNextImage from '../NextImage/FixedNextImage';

const personItemImageSize = 150;

const PartyMember = styled('div')`
  padding: 44px 25px 0;
  text-align: center;
  max-width: 300px;
  width: 300px;

  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.WeddingPartyPerson}
`;

const MemberName = styled('div')`
  font-weight: bolder;
  margin-bottom: 12px;

  ${themeFont('s6')}
`;

const ImageWrapper = styled('div')`
  background-color: rgb(151, 151, 151);
  border: 1px solid rgb(151, 151, 151);
  border-radius: 150px;
  height: ${personItemImageSize}px;
  margin: 0 auto 16px;
  width: ${personItemImageSize}px;
  overflow: hidden;
`;

const MemberDescription = styled('div')`
  line-height: 1.5;

  ${themeFont('s6')}
`;

interface PersonItemProps {
  item: Wws_PersonItem;
  className?: string | undefined;
}

function PersonItem({ item, className }: PersonItemProps) {
  const { description, id, name, role, photo } = item;

  const dangerouslySetInnerHTML = useMemo<
    | {
        __html: string;
      }
    | undefined
  >(
    () =>
      typeof description !== 'undefined' && description !== null
        ? {
            __html: cleanUserInput(description),
          }
        : undefined,
    [description]
  );

  if (!name) {
    return null;
  }

  const mediaUrl = photo?.mediaUrl || '';

  let photoParams: MediaUrlParams | undefined;

  if (mediaUrl) {
    photoParams = extractParamsFromMediaUrl(mediaUrl);
  }

  return (
    <PartyMember
      key={id}
      className={className}
      data-testid={`person-item-${id}`}
    >
      {photoParams ? (
        <ImageWrapper>
          <FixedNextImage
            src={photoParams.url || ''}
            crop={photoParams.crop || ''}
            height={personItemImageSize}
            width={personItemImageSize}
            alt={name}
            sizes={`${personItemImageSize}px`}
          />
        </ImageWrapper>
      ) : null}

      <MemberName>
        {name}
        {role ? ` - ${role}` : null}
      </MemberName>

      {description ? (
        <MemberDescription dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : null}
    </PartyMember>
  );
}

export default memo(PersonItem);
